import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, X, Building2, Users as UsersIcon } from "lucide-react";

/**
 * Phase D — My Access
 *
 * Read-only, human-readable summary of the logged-in client user's
 * authorization state. Every value comes from the server-provided
 * /auth/me response (effective_permissions, report_scope, role_name).
 * The client NEVER recomputes permission math — backend is authoritative.
 */
const SCOPE_LABEL = {
  self:    { title: "My reports only",     hint: "You see only screenings you personally ordered." },
  team:    { title: "My team's reports",   hint: "You see screenings ordered by your team." },
  company: { title: "Company-wide reports", hint: "You see every screening in your company." },
};

// Human-readable descriptors for each catalog permission — never expose
// internal keys like `reports.view_sensitive` on the user-facing surface.
const CAPABILITIES = [
  { key: "reports.order",           label: "Order new reports" },
  { key: "reports.view",            label: "View reports" },
  { key: "reports.download",        label: "Download report PDFs" },
  { key: "reports.view_sensitive",  label: "See sensitive PII (SSN, DOB, full financials)" },
  { key: "applicants.view",         label: "See applicant identity details" },
  { key: "documents.view",          label: "View uploaded documents" },
  { key: "billing.view",            label: "See billing & credit balance" },
  { key: "company_profile.view",    label: "See the company profile" },
];

export default function MyAccessPanel() {
  const { user, reportScope, roleName, companyLabel } = useAuth();
  const eff = new Set(user?.effective_permissions || []);
  const scope = SCOPE_LABEL[reportScope] || SCOPE_LABEL.self;

  return (
    <div className="space-y-6" data-testid="my-access-panel">
      <Card className="p-8 rounded-sm border border-[#0A192F]/10">
        <div className="flex items-start gap-6 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <div className="ntn-eyebrow text-slate-500">Role</div>
            <div className="font-serif text-2xl text-[#0A192F] mt-1" data-testid="my-access-role">
              {roleName || "—"}
            </div>
            <div className="text-xs text-[#4A5568] mt-1">Assigned by NTN. Contact support to change.</div>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="ntn-eyebrow text-slate-500">Report access</div>
            <div className="font-serif text-2xl text-[#0A192F] mt-1" data-testid="my-access-scope">
              {scope.title}
            </div>
            <div className="text-xs text-[#4A5568] mt-1">{scope.hint}</div>
          </div>
          <div className="flex-1 min-w-[220px]">
            <div className="ntn-eyebrow text-slate-500">Company</div>
            <div className="font-serif text-2xl text-[#0A192F] mt-1 flex items-center gap-2" data-testid="my-access-company">
              <Building2 className="h-5 w-5 text-slate-400" />{companyLabel || user?.company || "—"}
            </div>
            {user?.company_account_number && (
              <div className="text-xs text-[#4A5568] mt-1 font-mono">ACCT #{user.company_account_number}</div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-8 rounded-sm border border-[#0A192F]/10">
        <div className="flex items-center gap-2 mb-4">
          <UsersIcon className="h-4 w-4 text-slate-400" />
          <h3 className="ntn-h3">What you can do</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="my-access-capabilities">
          {CAPABILITIES.map((c) => {
            const allowed = eff.has(c.key);
            return (
              <div
                key={c.key}
                className={`flex items-center gap-3 rounded-sm border px-4 py-3 ${allowed ? "border-emerald-200 bg-emerald-50/30" : "border-slate-200 bg-slate-50/40"}`}
                data-testid={`cap-${c.key.replace(/\./g, "-")}-${allowed ? "yes" : "no"}`}
              >
                {allowed ? (
                  <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                ) : (
                  <X className="h-4 w-4 text-slate-400 shrink-0" />
                )}
                <div className="text-sm text-slate-800">{c.label}</div>
                <Badge className={`ml-auto text-[10px] ${allowed ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>
                  {allowed ? "Yes" : "No"}
                </Badge>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500 mt-4 italic">
          If a capability shows &ldquo;No&rdquo; and you need it, contact your NTN account
          manager. Client user permissions are managed by NTN.
        </p>
      </Card>
    </div>
  );
}
